import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  getWhatsappConversationsList,
  WhatsappAgentGroupPage,
} from '@/modules/lead-page/api/lead-page.v2.api';
import { WhatsappAgentGroup } from '@/modules/lead-page/dtos/index-v2';

const PAGE_SIZE = 5;

export const useWhatsappConversations = (
  journeyUid: string,
  options?: { enabled?: boolean; agentUids?: string[] },
) => {
  const agentUids = options?.agentUids?.length ? options.agentUids : undefined;

  const query = useInfiniteQuery({
    queryKey: ['whatsapp-conversations-list', journeyUid, agentUids ?? null],
    queryFn: ({ pageParam }: { pageParam?: number }) =>
      getWhatsappConversationsList(journeyUid, {
        page: pageParam ?? 1,
        pageSize: PAGE_SIZE,
        agentUids,
      }),
    getNextPageParam: (lastPage: WhatsappAgentGroupPage) =>
      lastPage.page * lastPage.pageSize < lastPage.total
        ? lastPage.page + 1
        : undefined,
    enabled: (options?.enabled ?? true) && !!journeyUid,
    // A 401 means the viewer has no access to this integration — the section
    // hides on it, so retrying would only delay that.
    retry: (failureCount, error) =>
      !(axios.isAxiosError(error) && error.response?.status === 401) &&
      failureCount < 3,
  });

  const agentGroups: WhatsappAgentGroup[] = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page?.results ?? []),
    [query.data],
  );

  const totalAgents = query.data?.pages?.[0]?.total ?? 0;

  return { ...query, agentGroups, totalAgents };
};
